import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MultiSelect } from '../components/FilterBar';

const STATUS_META = {
  'Serial Missing': { color:'var(--red)',   bg:'var(--red-bg)',   border:'var(--red-bd)',   label:'Serial Missing' },
  'Cost Not Found': { color:'var(--amber)', bg:'var(--amber-bg)', border:'var(--amber-bd)', label:'Cost Not Found' },
  'Partial Match':  { color:'var(--amber)', bg:'var(--amber-bg)', border:'var(--amber-bd)', label:'Partial Match'  },
};

const API   = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('bc_token') || '';

const PFX = 'scs_';
function getP(key) { return new URLSearchParams(window.location.search).get(PFX + key) || ''; }
function setP(key, val) {
  const sp = new URLSearchParams(window.location.search);
  const v  = Array.isArray(val) ? val.join('|') : (val || '');
  if (v && v !== 'ALL') sp.set(PFX + key, v); else sp.delete(PFX + key);
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}
function clearAllParams() {
  const sp = new URLSearchParams(window.location.search);
  ['s','st','bg','pg','mk'].forEach(k => sp.delete(PFX + k));
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}

export default function SerialCostStatus({ db }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [sortCol, setSortCol] = useState('SaleDate');
  const [sortDir, setSortDir] = useState('desc');

  const [search,       setSearchRaw]       = useState(() => getP('s'));
  const [filterStatus, setFilterStatusRaw] = useState(() => getP('st') || 'ALL');
  const [filterBGrp,   setFilterBGrpRaw]   = useState(() => { const v = getP('bg'); return v ? v.split('|') : []; });
  const [filterPGrp,   setFilterPGrpRaw]   = useState(() => { const v = getP('pg'); return v ? v.split('|') : []; });
  const [filterMake,   setFilterMakeRaw]   = useState(() => { const v = getP('mk'); return v ? v.split('|') : []; });

  const setSearch       = v => { setSearchRaw(v);       setP('s',  v); };
  const setFilterStatus = v => { setFilterStatusRaw(v); setP('st', v); };
  const setFilterBGrp   = v => { setFilterBGrpRaw(v);   setP('bg', v); };
  const setFilterPGrp   = v => { setFilterPGrpRaw(v);   setP('pg', v); };
  const setFilterMake   = v => { setFilterMakeRaw(v);   setP('mk', v); };

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/serial-cost-status?db=${db}`, { headers: { Authorization: `Bearer ${token()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [db]);

  useEffect(() => { if (db) fetchData(); }, [fetchData, db]);

  const allBGrps = useMemo(() => [...new Set(data.map(d => d.BGroup).filter(Boolean).sort())], [data]);

  const availablePGrps = useMemo(() => {
    const src = filterBGrp.length > 0 ? data.filter(d => filterBGrp.includes(d.BGroup)) : data;
    return [...new Set(src.map(d => d.PGroup).filter(Boolean).sort())];
  }, [data, filterBGrp]);

  const availableMakes = useMemo(() => {
    let src = data;
    if (filterBGrp.length > 0) src = src.filter(d => filterBGrp.includes(d.BGroup));
    if (filterPGrp.length > 0) src = src.filter(d => filterPGrp.includes(d.PGroup));
    return [...new Set(src.map(d => d.Make).filter(Boolean).sort())];
  }, [data, filterBGrp, filterPGrp]);

  // Cascade prune when parent filter changes
  useEffect(() => {
    if (filterPGrp.length > 0) {
      const valid = filterPGrp.filter(x => availablePGrps.includes(x));
      if (valid.length !== filterPGrp.length) { setFilterPGrpRaw(valid); setP('pg', valid); }
    }
  }, [availablePGrps]); // eslint-disable-line
  useEffect(() => {
    if (filterMake.length > 0) {
      const valid = filterMake.filter(x => availableMakes.includes(x));
      if (valid.length !== filterMake.length) { setFilterMakeRaw(valid); setP('mk', valid); }
    }
  }, [availableMakes]); // eslint-disable-line

  const counts = useMemo(() => {
    const c = {}; data.forEach(d => { c[d.CostStatus] = (c[d.CostStatus] || 0) + 1; }); return c;
  }, [data]);

  const stats = useMemo(() => ({
    total:     data.length,
    totalQty:  data.reduce((s, r) => s + (r.Qty || 0), 0),
    totalMiss: data.reduce((s, r) => s + (r.SerialRateMissing || 0), 0),
    notFound:  data.filter(d => d.CostStatus === 'Cost Not Found').length,
  }), [data]);

  const filtered = useMemo(() => {
    let d = data;
    if (filterStatus !== 'ALL') d = d.filter(r => r.CostStatus === filterStatus);
    if (filterBGrp.length)      d = d.filter(r => filterBGrp.includes(r.BGroup));
    if (filterPGrp.length)      d = d.filter(r => filterPGrp.includes(r.PGroup));
    if (filterMake.length)      d = d.filter(r => filterMake.includes(r.Make));
    if (search.trim()) {
      const q = search.toLowerCase();
      d = d.filter(r =>
        (r.ItemName||'').toLowerCase().includes(q) ||
        (r.VchNo||'').toLowerCase().includes(q) ||
        (r.Make||'').toLowerCase().includes(q)
      );
    }
    return [...d].sort((a, b) => {
      let av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
      if (sortCol === 'SaleDate') return sortDir === 'asc' ? new Date(av)-new Date(bv) : new Date(bv)-new Date(av);
      if (typeof av === 'number') return sortDir === 'asc' ? av-bv : bv-av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [data, filterStatus, filterBGrp, filterPGrp, filterMake, search, sortCol, sortDir]);

  const filteredQty  = useMemo(() => filtered.reduce((s, r) => s + (r.Qty || 0), 0), [filtered]);
  const filteredMiss = useMemo(() => filtered.reduce((s, r) => s + (r.SerialRateMissing || 0), 0), [filtered]);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }
  function Th({ col, children }) {
    const active = sortCol === col;
    return (
      <th className={active ? 'sorted' : ''} onClick={() => handleSort(col)}>
        {children}<span className="sort-arrow">{active ? (sortDir==='asc'?'↑':'↓') : '↕'}</span>
      </th>
    );
  }

  const anyFilter = filterStatus !== 'ALL' || filterBGrp.length || filterPGrp.length || filterMake.length || search;
  function clearAll() {
    setSearchRaw(''); setFilterStatusRaw('ALL'); setFilterBGrpRaw([]); setFilterPGrpRaw([]); setFilterMakeRaw([]);
    clearAllParams();
  }

  return (
    <div className="fade-up">
      <div className="page-head">
        <div className="page-title">Serial Cost Status</div>
        <div className="page-sub">
          Sale rows where serial cost lookup failed or is partial · Cost Matched excluded · {db}
          {' '}· <span style={{ color:'var(--amber)' }}>Note: cross-year lookup not available here</span>
        </div>
      </div>

      <div className="summary-cards">
        <div className="sum-card">
          <div className="sum-card-label">Issues Total</div>
          <div className="sum-card-value red">{stats.total.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">sale rows with cost issues</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Total Qty</div>
          <div className="sum-card-value orange">{stats.totalQty.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">units affected</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Rate Missing</div>
          <div className="sum-card-value red">{stats.totalMiss.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">serials without purchase rate</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Cost Not Found</div>
          <div className="sum-card-value orange">{stats.notFound.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">zero rate matches</div>
        </div>
      </div>

      {error && (
        <div className="error-bar">
          <span className="error-icon">⚠</span>
          <div><div>Failed to load data</div><div className="error-detail">{error}</div></div>
        </div>
      )}

      <div className="stats-row-scroll">
        <button className={`stat-chip ${filterStatus==='ALL'?'active':''}`}
          style={{ color:'var(--text2)', borderColor:'var(--border)', background:filterStatus==='ALL'?'var(--surface3)':'transparent' }}
          onClick={() => setFilterStatus('ALL')}>
          <span className="stat-dot" style={{ background:'var(--text3)' }} />ALL ({data.length})
        </button>
        {Object.entries(STATUS_META).map(([key, m]) => (
          <button key={key} className={`stat-chip ${filterStatus===key?'active':''}`}
            style={{ color:m.color, background:filterStatus===key?m.bg:'transparent', borderColor:m.border }}
            onClick={() => setFilterStatus(filterStatus === key ? 'ALL' : key)}>
            <span className="stat-dot" style={{ background:m.color }} />{m.label} ({counts[key]||0})
          </button>
        ))}
      </div>

      <div className="toolbar">
        <div className="toolbar-row-a">
          <div className="search-box">
            <svg className="search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input placeholder="Search item, voucher, make…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className={`btn-refresh ${loading?'spinning':''}`} onClick={fetchData} disabled={loading}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15"/>
            </svg>
            <span>Refresh</span>
          </button>
        </div>
        <div className="toolbar-row-b">
          <MultiSelect label="Base Grp"   options={allBGrps}       selected={filterBGrp}
            onChange={v => { setFilterBGrp(v); setFilterPGrpRaw([]); setP('pg',[]); setFilterMakeRaw([]); setP('mk',[]); }} />
          <MultiSelect label="Parent Grp" options={availablePGrps} selected={filterPGrp}
            onChange={v => { setFilterPGrp(v); setFilterMakeRaw([]); setP('mk',[]); }} />
          <MultiSelect label="Brand/Make" options={availableMakes} selected={filterMake} onChange={setFilterMake} />
          <button className="btn-reset" onClick={clearAll} disabled={!anyFilter}>↺ Reset</button>
          <span className="count-badge">{filtered.length} rows · {filteredQty} qty · {filteredMiss} rate missing</span>
        </div>
      </div>

      {loading && !data.length ? (
        <div className="loading-state"><div className="spinner" /><span>Loading cost status data…</span></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <Th col="CostStatus">Status</Th>
                <Th col="SaleDate">Sale Date</Th>
                <Th col="VchNo">Vch No</Th>
                <Th col="ItemName">Item</Th>
                <Th col="Make">Make</Th>
                <Th col="BGroup">Base Grp</Th>
                <Th col="PGroup">Parent Grp</Th>
                <Th col="GGroup">Group</Th>
                <Th col="Qty">Qty</Th>
                <Th col="TotalSerials">Serials</Th>
                <Th col="SerialRateMatched">✓ Rate</Th>
                <Th col="SerialRateMissing">✗ Missing</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={12}>
                  <div className="empty-state">
                    <div className="empty-icon">✅</div>
                    <h3>No issues found</h3>
                    <p>All filtered records have matched costs</p>
                  </div>
                </td></tr>
              ) : filtered.map((r, i) => {
                const m = STATUS_META[r.CostStatus] || { color:'var(--text2)', bg:'transparent', border:'var(--border)' };
                return (
                  <tr key={i}>
                    <td><span className="badge" style={{ color:m.color, background:m.bg, borderColor:m.border }}>{m.label || r.CostStatus}</span></td>
                    <td className="mono dim" style={{ fontSize:11 }}>{r.SaleDate}</td>
                    <td className="mono">{r.VchNo}</td>
                    <td className="wrap">{r.ItemName}</td>
                    <td className="dim">{r.Make || <span className="muted">—</span>}</td>
                    <td className="dim">{r.BGroup || <span className="muted">—</span>}</td>
                    <td className="dim">{r.PGroup || <span className="muted">—</span>}</td>
                    <td className="dim">{r.GGroup || <span className="muted">—</span>}</td>
                    <td className="num" style={{ color:'var(--amber)', fontWeight:600 }}>{r.Qty}</td>
                    <td className="num dim">{r.TotalSerials || <span className="muted">—</span>}</td>
                    <td className="num" style={{ color:(r.SerialRateMatched||0)>0?'var(--green)':'var(--text3)' }}>
                      {r.SerialRateMatched ?? <span className="muted">—</span>}
                    </td>
                    <td className="num" style={{ color:(r.SerialRateMissing||0)>0?'var(--red)':'var(--text3)', fontWeight:(r.SerialRateMissing||0)>0?700:400 }}>
                      {r.SerialRateMissing ?? <span className="muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
